<template>
  <div>
    <!-- Loading State -->
    <div v-if="loading" class="text-center pa-6">
      <v-progress-circular
        indeterminate
        color="primary"
        size="40"
        width="4"
        class="mb-3"
      ></v-progress-circular>
      <div class="caption grey--text">Loading enrollment data...</div>
    </div>
    
    <div v-else>
      <!-- Plan Section -->
      <div v-if="planName" class="mb-6">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="primary" class="mr-3">
            <v-icon color="white" size="20">mdi-file-document-outline</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Plan</h3>
        </div>
        <v-card outlined class="pa-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-check-circle</v-icon>
            <span class="subtitle-1 font-weight-medium">{{ planName }}</span>
          </div>
        </v-card>
      </div>
      
      <!-- Benefits Section -->
      <div class="mb-6">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="success" class="mr-3">
            <v-icon color="white" size="20">mdi-heart-pulse</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Benefits</h3>
        </div>
        <div v-if="benefitsList && benefitsList.length > 0" class="d-flex flex-wrap">
          <v-chip
            v-for="(benefit, index) in benefitsList"
            :key="'b-' + index"
            class="mr-2 mb-2"
            color="success"
            outlined
          >
            <v-icon small left color="success">mdi-check-circle</v-icon>
            <span style="color: #4caf50;">{{ benefit }}</span>
          </v-chip>
        </div>
        <div v-else>
          <v-card outlined class="pa-3">
            <div class="caption grey--text text--lighten-1">
              <v-icon small class="mr-1">mdi-information-outline</v-icon>
              No benefits found for this member.
            </div>
          </v-card>
        </div>
      </div>
      
      <!-- PCP Section (only if plan is PHC) -->
      <div v-if="hasPHCPlan === true" class="mb-6">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="success" class="mr-3">
            <v-icon color="white" size="20">mdi-medical-bag</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Primary Care Provider (PCP)</h3>
        </div>
        
        <v-card
          class="mb-3 pcp-card"
          outlined
          :class="pcpData ? 'pcp-selected' : 'pcp-unselected'"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-start">
              <v-avatar
                size="48"
                :color="pcpData ? 'success' : 'grey lighten-2'"
                class="mr-4"
              >
                <v-icon
                  :color="pcpData ? 'white' : 'grey darken-1'"
                  size="24"
                >
                  mdi-medical-bag
                </v-icon>
              </v-avatar>
              <div
                class="flex-grow-1"
                :class="{ 'clickable-provider': pcpData }"
                @click="pcpData ? showProviderDetails(pcpData, 'PCP') : null"
              >
                <div class="d-flex align-center mb-2">
                  <h4 class="subtitle-1 font-weight-bold mr-2 mb-0">
                    Primary Care Provider
                  </h4>
                  <v-chip
                    v-if="pcpData"
                    x-small
                    color="success"
                    text-color="white"
                  >
                    <v-icon x-small left>mdi-check</v-icon>
                    Selected
                  </v-chip>
                  <v-chip
                    v-else
                    x-small
                    color="warning"
                    text-color="white"
                  >
                    <v-icon x-small left>mdi-alert</v-icon>
                    Not Selected
                  </v-chip>
                </div>
                <div v-if="pcpData" class="pl-4">
                  <div class="body-1 font-weight-medium mb-1">
                    {{ pcpData.PROVIDER_NAME }}
                    <v-icon v-if="pcpData" small class="ml-1 grey--text">mdi-information-outline</v-icon>
                  </div>
                  <div v-if="pcpData.SERVICE_LOCATION_CITY" class="caption grey--text d-flex align-center">
                    <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                    {{ pcpData.SERVICE_LOCATION_CITY }}, {{ pcpData.SERVICE_LOCATION_STATE }}
                  </div>
                  <div v-if="pcpData.BILLING_PHONE" class="caption grey--text d-flex align-center mt-1">
                    <v-icon x-small class="mr-1">mdi-phone</v-icon>
                    {{ pcpData.BILLING_PHONE }}
                  </div>
                  <div v-if="pcpData" class="caption primary--text mt-1">
                    <v-icon x-small class="mr-1">mdi-cursor-pointer</v-icon>
                    Click to view full details
                  </div>
                </div>
                <div v-else class="pl-4">
                  <div class="body-2 grey--text">
                    No PCP has been selected yet.
                  </div>
                </div>
              </div>
              <div class="ml-auto">
                <v-btn
                  v-if="pcpData"
                  color="success"
                  outlined
                  small
                  @click.stop="$emit('change-pcp', member)"
                >
                  <v-icon small left>mdi-pencil</v-icon>
                  Change
                </v-btn>
                <v-btn
                  v-else
                  color="success"
                  small
                  @click.stop="handleSelectPCP"
                >
                  <v-icon small left>mdi-plus-circle</v-icon>
                  Select
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
      
      <!-- Specialists Section -->
      <div v-if="specialistsList && specialistsList.length > 0">
        <div class="d-flex align-center mb-3">
          <v-avatar size="32" color="primary" class="mr-3">
            <v-icon color="white" size="20">mdi-doctor</v-icon>
          </v-avatar>
          <h3 class="title font-weight-bold mb-0">Specialists</h3>
        </div>
        
        <!-- Show existing specialists from enrollment -->
        <div>
          <v-card
            v-for="(specialist, index) in specialistsList"
            :key="'s-' + index"
            class="mb-3 specialist-card"
            outlined
            :class="specialist.provider ? 'specialist-selected' : 'specialist-unselected'"
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-start">
                <v-avatar
                  size="48"
                  :color="specialist.provider ? 'primary' : 'grey lighten-2'"
                  class="mr-4"
                >
                  <v-icon
                    :color="specialist.provider ? 'white' : 'grey darken-1'"
                    size="24"
                  >
                    mdi-stethoscope
                  </v-icon>
                </v-avatar>
                <div
                  class="flex-grow-1"
                  :class="{ 'clickable-provider': specialist.provider }"
                  @click="specialist.provider ? showProviderDetails(specialist.provider, specialist.specialty) : null"
                >
                  <div class="d-flex align-center mb-2">
                    <h4 class="subtitle-1 font-weight-bold mr-2 mb-0">
                      {{ specialist.specialty || 'Specialist' }}
                    </h4>
                    <v-chip
                      v-if="specialist.provider"
                      x-small
                      color="success"
                      text-color="white"
                    >
                      <v-icon x-small left>mdi-check</v-icon>
                      Selected
                    </v-chip>
                    <v-chip
                      v-else
                      x-small
                      color="warning"
                      text-color="white"
                    >
                      <v-icon x-small left>mdi-alert</v-icon>
                      Not Selected
                    </v-chip>
                  </div>
                  <div v-if="specialist.provider" class="pl-4">
                    <div class="body-1 font-weight-medium mb-1">
                      {{ specialist.provider.PROVIDER_NAME }}
                      <v-icon v-if="specialist.provider" small class="ml-1 grey--text">mdi-information-outline</v-icon>
                    </div>
                    <div v-if="specialist.provider.SERVICE_LOCATION_CITY" class="caption grey--text d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                      {{ specialist.provider.SERVICE_LOCATION_CITY }}, {{ specialist.provider.SERVICE_LOCATION_STATE }}
                    </div>
                    <div v-if="specialist.provider.BILLING_PHONE" class="caption grey--text d-flex align-center mt-1">
                      <v-icon x-small class="mr-1">mdi-phone</v-icon>
                      {{ specialist.provider.BILLING_PHONE }}
                    </div>
                    <div v-if="specialist.provider" class="caption primary--text mt-1">
                      <v-icon x-small class="mr-1">mdi-cursor-pointer</v-icon>
                      Click to view full details
                    </div>
                  </div>
                  <div v-else class="pl-4">
                    <div class="body-2 grey--text">
                      No specialist has been selected for this specialty yet.
                    </div>
                  </div>
                </div>
                <div class="ml-auto">
                  <v-btn
                    v-if="specialist.provider"
                    color="primary"
                    outlined
                    small
                    @click.stop="$emit('change-specialist', member, specialist.specialty)"
                  >
                    <v-icon small left>mdi-pencil</v-icon>
                    Change
                  </v-btn>
                  <v-btn
                    v-else
                    color="primary"
                    small
                    @click.stop="$emit('select-specialist', member, specialist.specialty)"
                  >
                    <v-icon small left>mdi-plus-circle</v-icon>
                    Select
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
    
    <!-- Provider Details Dialog -->
    <v-dialog
      v-model="showProviderDialog"
      max-width="600px"
      scrollable
    >
      <v-card v-if="selectedProvider">
        <v-card-title class="primary white--text">
          <v-icon class="mr-2" color="white">mdi-doctor</v-icon>
          <span class="font-weight-bold">Provider Details</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showProviderDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <!-- Provider Name and Specialty -->
          <div class="mb-4">
            <div class="text-h5 font-weight-bold mb-1">
              {{ selectedProvider.PROVIDER_NAME }}
            </div>
            <v-chip color="primary" outlined class="mt-1">
              <v-icon small left>mdi-stethoscope</v-icon>
              {{ selectedProviderSpecialty }}
            </v-chip>
          </div>
          
          <v-divider class="my-4"></v-divider>
          
          <!-- Provider ID -->
          <div v-if="selectedProvider.PROVIDER_ID" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-identifier</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Provider ID</span>
            </div>
            <div class="body-1 ml-8">{{ selectedProvider.PROVIDER_ID }}</div>
          </div>
          
          <!-- Address -->
          <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1 || selectedProvider.SERVICE_LOCATION_CITY" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-map-marker</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Address</span>
            </div>
            <div class="body-1 ml-8">
              <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_1">
                {{ selectedProvider.SERVICE_LOCATION_ADDRESS_1 }}
              </div>
              <div v-if="selectedProvider.SERVICE_LOCATION_ADDRESS_2" class="mt-1">
                {{ selectedProvider.SERVICE_LOCATION_ADDRESS_2 }}
              </div>
              <div v-if="selectedProvider.SERVICE_LOCATION_CITY" class="mt-1">
                {{ selectedProvider.SERVICE_LOCATION_CITY }}{{ selectedProvider.SERVICE_LOCATION_STATE ? ', ' + selectedProvider.SERVICE_LOCATION_STATE : '' }}
                {{ selectedProvider.SERVICE_LOCATION_ZIP || '' }}
              </div>
            </div>
          </div>
          
          <!-- Phone Numbers -->
          <div v-if="selectedProvider.BILLING_PHONE || selectedProvider.SERVICE_LOCATION_PHONE" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-phone</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Phone</span>
            </div>
            <div class="body-1 ml-8">
              <div v-if="selectedProvider.BILLING_PHONE" class="mb-1">
                <span class="caption grey--text mr-2">Billing:</span>
                <a :href="`tel:${selectedProvider.BILLING_PHONE}`" class="text-decoration-none">
                  {{ selectedProvider.BILLING_PHONE }}
                </a>
              </div>
              <div v-if="selectedProvider.SERVICE_LOCATION_PHONE">
                <span class="caption grey--text mr-2">Service Location:</span>
                <a :href="`tel:${selectedProvider.SERVICE_LOCATION_PHONE}`" class="text-decoration-none">
                  {{ selectedProvider.SERVICE_LOCATION_PHONE }}
                </a>
              </div>
            </div>
          </div>
          
          <!-- Languages -->
          <div v-if="selectedProvider.LANGUAGE || selectedProvider.LANGUAGES" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-translate</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Languages</span>
            </div>
            <div class="ml-8">
              <div v-if="selectedProvider.LANGUAGES && Array.isArray(selectedProvider.LANGUAGES) && selectedProvider.LANGUAGES.length > 0">
                <v-chip
                  v-for="(lang, index) in selectedProvider.LANGUAGES"
                  :key="index"
                  small
                  class="mr-2 mb-1"
                  color="primary"
                  outlined
                >
                  {{ lang }}
                </v-chip>
              </div>
              <div v-else-if="selectedProvider.LANGUAGE" class="body-1">
                <v-chip
                  small
                  color="primary"
                  outlined
                >
                  {{ selectedProvider.LANGUAGE }}
                </v-chip>
              </div>
            </div>
          </div>
          
          <!-- Primary Specialty -->
          <div v-if="selectedProvider.PRIMARY_SPECIALTY" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-hospital-box</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Primary Specialty</span>
            </div>
            <div class="body-1 ml-8">{{ selectedProvider.PRIMARY_SPECIALTY }}</div>
          </div>
          
          <!-- Coordinates (if available) -->
          <div v-if="selectedProvider.SERVICE_LOCATION_LAT && selectedProvider.SERVICE_LOCATION_LON" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2 grey--text">mdi-crosshairs-gps</v-icon>
              <span class="caption grey--text text--darken-1 font-weight-medium">Location</span>
            </div>
            <div class="body-2 ml-8 grey--text">
              Coordinates: {{ selectedProvider.SERVICE_LOCATION_LAT }}, {{ selectedProvider.SERVICE_LOCATION_LON }}
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            v-if="selectedProviderSpecialty === 'PCP'"
            color="success"
            outlined
            @click="$emit('change-pcp', member)"
          >
            <v-icon left>mdi-pencil</v-icon>
            Change PCP
          </v-btn>
          <v-btn
            v-else
            color="primary"
            outlined
            @click="$emit('change-specialist', member, selectedProviderSpecialty)"
          >
            <v-icon left>mdi-pencil</v-icon>
            Change Specialist
          </v-btn>
          <v-btn
            color="primary"
            @click="showProviderDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MemberEnrollmentDetails',
  props: {
    member: {
      type: Object,
      required: true
    },
    insurance: {
      type: String,
      required: true
    },
    enrollmentId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      enrollmentData: {
        benefits: [],
        specialists: [],
        pcp: null,
        planName: null
      },
      loading: true,
      showProviderDialog: false,
      selectedProvider: null,
      selectedProviderSpecialty: null,
      hasPHCPlan: false,
      benefitsList: [],
      specialistsList: [],
      pcpData: null,
      planName: null
    };
  },
  async mounted() {
    await this.loadEnrollmentData();
  },
  methods: {
    async loadEnrollmentData() {
      this.loading = true;
      try {
        // Add cache busting timestamp to ensure fresh data
        const timestamp = Date.now();
        let url;
        
        // If enrollmentId is provided, use it; otherwise use member.MEMBER_ID
        if (this.enrollmentId) {
          url = `/api/enrollment/enrollment/${this.enrollmentId}/member/${this.member.MEMBER_ID}?t=${timestamp}`;
        } else {
          url = `/api/enrollment/member/${this.insurance}/${this.member.MEMBER_ID}?t=${timestamp}`;
        }
        
        const response = await axios.get(url);
        if (response.data && response.data.enrollment) {
          const e = response.data.enrollment;
          
          // Debug: log planName from API
          console.log('📋 Plan Name from API:', e.planName);
          
          // Normalize data before calculation
          const benefits = Array.isArray(e.benefits) ? e.benefits : (e.benefits ? [e.benefits] : []);
          const specialists = Array.isArray(e.specialists) ? e.specialists : 
                        (e.specialists && typeof e.specialists === 'object' ? 
                         Object.keys(e.specialists).map(s => ({ specialty: s, provider: e.specialists[s] })) : []);
          
          // Calculate hasPHCPlan from API response
          // The backend checks if any benefit has planCode starting with "PHC"
          let hasPHC = false;
          if (e.hasPHCPlan !== undefined) {
            // Use the hasPHCPlan flag from the API (based on planCode in enrollmentplans)
            hasPHC = Boolean(e.hasPHCPlan);
          } else {
            // Fallback: check planName if hasPHCPlan is not available
            const pn = e.planName;
            if (pn) {
              const pns = String(pn).toUpperCase();
              if (pns.indexOf('PHC') >= 0) hasPHC = true;
            }
          }
          
          // Set all values at once to minimize reactivity triggers
          // CRITICAL FIX: Create completely non-reactive copies using JSON serialization
          // This prevents Vue DevTools from causing infinite loops when inspecting deeply nested objects
          // The DevTools tries to traverse all reactive properties, causing crashes
          this.hasPHCPlan = hasPHC;
          
          // Create non-reactive copies to break deep reactivity chains
          // JSON serialization creates plain objects that Vue won't make deeply reactive
          try {
            this.benefitsList = Array.isArray(benefits) ? JSON.parse(JSON.stringify(benefits)) : [];
            this.specialistsList = Array.isArray(specialists) ? JSON.parse(JSON.stringify(specialists)) : [];
            this.pcpData = e.pcp && typeof e.pcp === 'object' ? JSON.parse(JSON.stringify(e.pcp)) : null;
          } catch (jsonError) {
            // Fallback to shallow copies if JSON serialization fails (e.g., circular refs)
            this.benefitsList = Array.isArray(benefits) ? [...benefits] : [];
            this.specialistsList = Array.isArray(specialists) ? specialists.map(s => 
              (s && typeof s === 'object') ? { ...s } : s
            ) : [];
            this.pcpData = e.pcp && typeof e.pcp === 'object' ? { ...e.pcp } : null;
          }
          
          // Store planName separately for easier access
          // Handle both null, undefined, and empty string
          const planNameValue = e.planName && String(e.planName).trim() ? String(e.planName).trim() : null;
          this.planName = planNameValue;
          
          console.log('📋 Plan Name stored in component:', this.planName);
          
          // Keep enrollmentData for backward compatibility
          // These are now plain objects, not deeply reactive, preventing DevTools crashes
          this.enrollmentData = {
            benefits: this.benefitsList,
            specialists: this.specialistsList,
            pcp: this.pcpData,
            planName: this.planName
          };
        }
      } catch (error) {
        // If enrollment doesn't exist, show empty state
        this.planName = null;
        this.enrollmentData = {
          benefits: [],
          specialists: [],
          pcp: null,
          planName: null
        };
        this.hasPHCPlan = false;
        this.benefitsList = [];
        this.specialistsList = [];
        this.pcpData = null;
      } finally {
        this.loading = false;
      }
    },
    handleSelectPCP() {
      this.$emit('select-pcp', this.member);
    },
    async showProviderDetails(provider, specialty) {
      this.selectedProvider = provider;
      this.selectedProviderSpecialty = specialty;
      this.showProviderDialog = true;
      
      // Always fetch complete provider details from backend to get all fields
      if (provider && provider.PROVIDER_ID) {
        try {
          const response = await axios.get(`/api/provider/${provider.PROVIDER_ID}`);
          if (response.data && response.data.length > 0) {
            // Merge complete provider data with existing data
            const completeProvider = response.data[0];
            this.selectedProvider = {
              ...provider,
              ...completeProvider
            };
          }
        } catch (error) {
          // Continue with existing provider data if fetch fails
        }
      }
    }
  }
};
</script>

<style scoped>
.specialist-card {
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.specialist-card:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.specialist-selected {
  border-left-color: #4caf50 !important;
  background-color: #f1f8f4;
}

.specialist-unselected {
  border-left-color: #ff9800 !important;
  background-color: #fff8f0;
}

.specialist-empty-card {
  border: 2px dashed #e0e0e0;
  background-color: #fafafa;
}

.clickable-provider {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-provider:hover {
  opacity: 0.8;
}

.pcp-card {
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.pcp-card:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.pcp-selected {
  border-left-color: #4caf50 !important;
  background-color: #f1f8f4;
}

.pcp-unselected {
  border-left-color: #ff9800 !important;
  background-color: #fff8f0;
}
</style>


